import { useState, useEffect, useCallback } from 'react';
import useSyncedStorage from './useSyncedStorage';

const ACTIVE_KEY = 'sleekHearActivePlanId';

const newId = () => `${Date.now().toString(36)}${Math.random().toString(36).slice(2, 7)}`;
const emptyPlan = (name = 'Sunday Service') => ({ id: newId(), name, updatedAt: Date.now(), items: [] });

// Stored (or synced) plans -> state; always at least one plan.
const hydratePlans = (stored) => (Array.isArray(stored) && stored.length > 0 ? stored : [emptyPlan()]);

function loadActiveId() {
  try {
    return localStorage.getItem(ACTIVE_KEY);
  } catch {
    return null;
  }
}

/**
 * Service run-sheet state: named plans of ordered items
 * ({ kind: 'scripture' | 'song' | 'text', title, reference?, version?, content? })
 * persisted in localStorage and synced per login (see syncService), plus a
 * "current" pointer for stepping through. Which plan is active is per computer.
 */
export default function useServicePlan() {
  const [plans, setPlans] = useSyncedStorage('plans', hydratePlans);
  const [storedActiveId, setActivePlanId] = useState(loadActiveId);
  const [currentIndex, setCurrentIndex] = useState(-1);
  // A synced plan list may no longer contain the remembered plan.
  const activePlanId = plans.some(p => p.id === storedActiveId) ? storedActiveId : plans[0].id;

  useEffect(() => {
    try {
      localStorage.setItem(ACTIVE_KEY, activePlanId);
    } catch {
      // storage unavailable — selection just won't persist
    }
  }, [activePlanId]);

  const plan = plans.find(p => p.id === activePlanId) || plans[0];
  const items = plan.items;

  const updateActive = useCallback((fn) => {
    setPlans(prev => prev.map(p => (p.id === activePlanId ? { ...p, items: fn(p.items), updatedAt: Date.now() } : p)));
  }, [activePlanId, setPlans]);

  const addItem = useCallback((item) => {
    updateActive(list => [...list, { ...item, id: newId() }]);
  }, [updateActive]);

  const removeItem = useCallback((id) => {
    const idx = items.findIndex(i => i.id === id);
    if (idx === -1) return;
    updateActive(list => list.filter(i => i.id !== id));
    // Keep the pointer on the same item, or the nearest one if it was removed.
    setCurrentIndex(cur => {
      if (cur < 0) return cur;
      if (idx < cur) return cur - 1;
      if (idx === cur) return Math.min(cur, items.length - 2);
      return cur;
    });
  }, [items, updateActive]);

  const moveItem = useCallback((id, delta) => {
    const from = items.findIndex(i => i.id === id);
    const to = from + delta;
    if (from === -1 || to < 0 || to >= items.length) return;
    updateActive(list => {
      const next = [...list];
      const [moved] = next.splice(from, 1);
      next.splice(to, 0, moved);
      return next;
    });
    setCurrentIndex(cur => {
      if (cur === from) return to;
      if (cur === to) return from;
      return cur;
    });
  }, [items, updateActive]);

  const renamePlan = useCallback((name) => {
    setPlans(prev => prev.map(p => (p.id === activePlanId ? { ...p, name: name || 'Untitled', updatedAt: Date.now() } : p)));
  }, [activePlanId, setPlans]);

  const createPlan = useCallback((name) => {
    const p = emptyPlan(name || 'New Plan');
    setPlans(prev => [...prev, p]);
    setActivePlanId(p.id);
    setCurrentIndex(-1);
  }, [setPlans]);

  const deletePlan = useCallback(() => {
    const rest = plans.filter(p => p.id !== activePlanId);
    const next = rest.length > 0 ? rest : [emptyPlan()];
    setPlans(next);
    setActivePlanId(next[0].id);
    setCurrentIndex(-1);
  }, [plans, activePlanId, setPlans]);

  const selectPlan = useCallback((id) => {
    setActivePlanId(id);
    setCurrentIndex(-1);
  }, []);

  const clampedStep = useCallback((delta) => {
    const next = currentIndex + delta;
    if (items.length === 0 || next < 0 || next >= items.length) return null;
    setCurrentIndex(next);
    return items[next];
  }, [currentIndex, items]);

  return {
    plans, plan, items, currentIndex, setCurrentIndex,
    addItem, removeItem, moveItem,
    renamePlan, createPlan, deletePlan, selectPlan,
    next: () => clampedStep(1),
    prev: () => clampedStep(-1),
    canNext: items.length > 0 && currentIndex < items.length - 1,
    canPrev: currentIndex > 0,
  };
}
